import {ChangeDetectionStrategy, Component, computed, effect, inject, signal, viewChild} from '@angular/core';
import {TranslatePipe} from '@ngx-translate/core';
import {FormControl} from '@angular/forms';
import {toSignal} from '@angular/core/rxjs-interop';
import {debounceTime} from 'rxjs';
import {CreateFeatureRequest, FeatureSummary, UpdateFeatureRequest} from '../../../../../api';
import {FeatureStoreService} from '../../services/feature-store.service';
import {ElciaButton} from '../../../../components/buttons/button/button';
import {Search} from '../../../../components/search/search';
import {ElciaDialog} from '../../../../components/dialog/dialog';
import {Loader} from '../../../../components/loader/loader';
import {FeatureGrid} from '../feature-grid/feature-grid';
import {FeatureForm} from '../feature-form/feature-form';
import {FeatureFormValue} from '../../models/feature-form-value';
import {ConfirmService} from '../../../../services/confirm.service';

@Component({
  selector: 'elcia-feature-home',
  imports: [TranslatePipe, ElciaButton, Search, ElciaDialog, Loader, FeatureGrid, FeatureForm],
  templateUrl: './feature-home.html',
  providers: [FeatureStoreService],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FeatureHome {
  protected readonly store = inject(FeatureStoreService);
  private readonly confirmService = inject(ConfirmService);

  featureForm = viewChild<FeatureForm>(FeatureForm);
  isLoading = this.store.isLoading;
  isLoadingSelected = this.store.isLoadingSelected;
  features = this.store.features;

  protected isFormDisplayed = signal(false);
  private openMode = signal<'create' | 'edit'>('create');

  searchControl = new FormControl('', {nonNullable: true});

  private readonly searchValue = toSignal(
    this.searchControl.valueChanges.pipe(debounceTime(300)),
    {initialValue: this.searchControl.value}
  );

  private readonly syncSearch = effect(() => {
    this.store.searchTerm.set(this.searchValue() || null);
  });

  protected isFormSaving = computed(() =>
    this.store.createFeatureMutation.isPending() || this.store.updateFeatureMutation.isPending()
  );
  protected isEditMode = computed(() => this.openMode() === 'edit');

  protected showForm(): void {
    this.store.createFeatureMutation.reset();
    this.openMode.set('create');
    this.store.selectedFeatureId.set(null);
    this.isFormDisplayed.set(true);
  }

  protected closeForm(): void {
    this.isFormDisplayed.set(false);
    this.store.selectedFeatureId.set(null);
  }

  protected onDialogSave(): void {
    this.featureForm()?.submit();
  }

  protected onFeatureSelect(feature: FeatureSummary): void {
    this.store.updateFeatureMutation.reset();
    this.openMode.set('edit');
    this.store.selectedFeatureId.set(feature.id);
  }

  protected onFeatureDelete(feature: FeatureSummary): void {
    this.confirmService.confirm({
      title: 'entity.feature',
      message: feature.nameFr,
      accept: async () => {
        await this.store.deleteFeatureMutation.mutateAsync(feature.id);
      }
    });
  }

  protected deleteFeature(): void {
    const feature = this.store.selectedFeature();
    if (!feature?.id) return;
    this.confirmService.confirm({
      title: 'entity.feature',
      message: feature.nameFr,
      accept: async () => {
        await this.store.deleteFeatureMutation.mutateAsync(feature.id);
        this.closeForm();
      }
    });
  }

  protected submit(formValue: FeatureFormValue): void {
    const isEdition = this.openMode() === 'edit' && formValue.id !== null;

    if (isEdition) {
      const body: UpdateFeatureRequest = {
        nameFr: formValue.nameFr,
        nameEn: formValue.nameEn,
        nameNl: formValue.nameNl,
      };
      this.store.updateFeatureMutation.mutate({id: formValue.id!, body});
    } else {
      const request: CreateFeatureRequest = {
        nameFr: formValue.nameFr,
        nameEn: formValue.nameEn,
        nameNl: formValue.nameNl,
      };
      this.store.createFeatureMutation.mutate(request);
    }
  }

  private readonly closeFormOnSuccess = effect(() => {
    const createSuccess = this.store.createFeatureMutation.isSuccess();
    const updateSuccess = this.store.updateFeatureMutation.isSuccess();
    if (createSuccess || updateSuccess) {
      this.closeForm();
    }
  });

  private readonly openFormOnEdit = effect(() => {
    const selected = this.store.selectedFeature();
    if (!selected) return;
    this.isFormDisplayed.set(true);
  });
}
